import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { STATS, SERVICES, PROJECTS, CLIENTS, PROCESS, COMPANY } from "../lib/data";

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-[88vh] flex items-end overflow-hidden bg-[#0A192F] text-white">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2400&q=85"
            alt="Commercial interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/75 via-[#0A192F]/50 to-[#0A192F]/20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pt-32 pb-24">
          <p className="text-xs uppercase tracking-[0.35em] text-yellow-400 reveal reveal-1">
            Turnkey Interior Contractor · Since {COMPANY.founded}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mt-8 max-w-4xl reveal reveal-2">
            <span className="text-yellow-400">Commercial interiors,</span><br />
            <span className="text-yellow-400">delivered end to end.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/80 reveal reveal-3">
            Alex Interiors is a turnkey interior contractor executing bank branches, insurance offices, financial institutions and retail stores across India.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 reveal reveal-4">
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A192F] px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white transition-colors"
            >
              Get in Touch <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/projects"
              data-testid="hero-cta-portfolio"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm tracking-wide hover:bg-white hover:text-[#0A192F] transition-colors"
            >
              See Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#E2E8F0]">
          {STATS.map((s) => (
            <div key={s.label} className="py-12 px-4 text-center">
              <p className="font-heading text-4xl sm:text-5xl font-bold text-[#0A192F]">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-[#475569] mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[#FAFAFA] py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Who we are</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-6 leading-tight">
              A one-stop turnkey solution.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 text-lg text-[#475569] leading-relaxed">
            <p>
              For over a decade, Alex Interiors has been the single, accountable partner behind some of India's most demanding commercial fit-outs — bringing design, carpentry, civil, MEP, networking and life-safety together under one roof, one contract, one quality benchmark.
            </p>
            <Link to="/about" data-testid="home-about-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] link-line">
              About the company <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-24 lg:py-28 border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="eyebrow">What we do</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">Eight services. One team.</h2>
            </div>
            <Link to="/services" className="text-sm font-semibold link-line text-[#0A192F]">
              All services <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="bg-white p-8 min-h-[180px]">
                <span className="text-xs text-[#C5A059] tracking-widest">0{i + 1}</span>
                <h3 className="font-heading text-lg font-semibold mt-3 text-[#0A192F]">{s.title}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.short}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="bg-[#FAFAFA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="eyebrow">Trusted by</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">
              Banks, insurers and brands across India.
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-10 items-center">
            {CLIENTS.map((c) => (
              <div key={c.name} className="flex flex-col items-center justify-center text-center">
                <img
                  src={c.logo}
                  alt={c.name}
                  className="logo-greyscale h-10 max-w-[140px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#475569]">{c.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#0A192F] text-white py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">How we work</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">Three simple stages.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {PROCESS.map((p) => (
              <div key={p.n} className="bg-[#0A192F] p-10">
                <p className="font-heading text-5xl font-bold text-[#C5A059]">{p.n}</p>
                <h3 className="font-heading text-2xl font-semibold mt-4">{p.t}</h3>
                <p className="text-white/70 mt-3 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section className="bg-white py-24 lg:py-28 border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="eyebrow">Selected work</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4">Recent projects.</h2>
            </div>
            <Link to="/projects" className="text-sm font-semibold link-line text-[#0A192F]">
              View all projects <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROJECTS.slice(0, 6).map((p) => (
              <article key={p.id} className="group bg-white border border-[#E2E8F0] hover:border-[#C5A059] transition-colors duration-500">
                <div className="img-zoom aspect-[16/10]">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-xs uppercase tracking-widest text-[#C5A059]">{p.category}</p>
                  <h3 className="font-heading text-lg font-semibold text-[#0A192F] mt-2">{p.title}</h3>
                  <p className="text-sm text-[#475569] mt-1">{p.location}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FAFAFA] py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow">Start a project</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4 leading-tight">
              Have a branch, office or store to fit out?
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-8 py-4 text-sm font-semibold hover:bg-[#C5A059] hover:text-[#0A192F] transition-colors"
            >
              Get in Touch <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
